export interface ForecastProvider {
  key: string; name: string; model_id: string; revision: string; usage: string;
  max_context: number; max_horizon: number; max_tuning_horizon: number;
  quantiles: number[]; tuning_modes: string[]; tuning_implementation: string;
  runtime_kind?: 'foundation_forecast' | 'statistical_forecast';
}

export interface ForecastSpec {
  kind: 'foundation_forecast' | 'statistical_forecast'; schema_version: 1; provider: string;
  model_id?: string; revision?: string;
  context_length: number; prediction_length: number; quantiles: number[];
  program?: { code: string; parameters?: Record<string, unknown>; primary?: string; timeout_seconds?: number };
  execution?: Record<string, unknown>;
  observation?: Record<string, unknown>;
  data: {
    timestamp_column: string; series_columns: string[]; target_columns: string[];
    past_covariates: string[]; future_covariates: string[];
    frequency: string; calendar: string; missing_policy: string;
    available_at_column?: string | null;
    future_available_at_column?: string | null;
    seasonal_period?: number | null;
  };
}

export interface ForecastRow {
  series_id: string; target: string; forecast_origin: string; timestamp: string;
  horizon: number; prediction: number; actual?: number | null;
  quantiles: Record<string, number>; baseline: number;
  display_quantiles?: Record<string, number>; display_prediction?: number; display_actual?: number | null; display_baseline?: number; unit?: string;
  quantiles_bps?: Record<string, number>; prediction_bps?: number;
}

export const defaultForecastSpec: ForecastSpec = {
  kind: 'foundation_forecast', schema_version: 1, provider: 'timesfm3',
  context_length: 512, prediction_length: 24, quantiles: [0.1, 0.5, 0.9],
  data: { timestamp_column: 'timestamp', series_columns: ['symbol'], target_columns: ['close'],
    past_covariates: [], future_covariates: [], frequency: '1h', calendar: 'continuous', missing_policy: 'mask' },
};

export function validateForecastForm(spec: ForecastSpec, provider: ForecastProvider | undefined, mode: string): string | null {
  if (!provider) return 'Choose a model.';
  if (!Number.isInteger(spec.context_length) || spec.context_length < 32 || spec.context_length > provider.max_context) return `Lookback must be 32–${provider.max_context} observations.`;
  const maxHorizon = ['pretrained', 'frozen'].includes(mode) ? provider.max_horizon : provider.max_tuning_horizon;
  if (!Number.isInteger(spec.prediction_length) || spec.prediction_length < 1 || spec.prediction_length > maxHorizon) return `Forecast horizon must be 1–${maxHorizon} observations for this mode.`;
  if (!spec.data.timestamp_column || !spec.data.target_columns.length) return 'Map the timestamp and target columns.';
  const columns = [spec.data.timestamp_column, ...spec.data.series_columns, ...spec.data.target_columns, ...spec.data.past_covariates, ...spec.data.future_covariates];
  if (new Set(columns).size !== columns.length) return 'Each column must have one role.';
  for (const field of ['available_at_column', 'future_available_at_column'] as const) {
    const column = spec.data[field];
    if (column != null && (!column || columns.includes(column))) return 'Availability timestamps must use separate columns from model inputs.';
  }
  return null;
}

export function selectForecastProvider(spec: ForecastSpec, provider: ForecastProvider): ForecastSpec {
  const result = { ...spec, kind: provider.runtime_kind || 'foundation_forecast' as ForecastSpec['kind'], provider: provider.key, model_id: provider.model_id, revision: provider.revision };
  if (provider.key === 'python_forecast') result.program ||= { code: defaultForecastProgram, parameters: {}, primary: 'prediction' };
  else { delete result.program; delete result.execution; delete result.observation; }
  return result;
}

export function chartForecasts(rows: ForecastRow[], series: string, origin: string) {
  return rows.filter(row => `${row.series_id} / ${row.target}` === series && row.forecast_origin === origin)
    .sort((a, b) => a.horizon - b.horizon).map(row => {
      const quantiles = row.display_quantiles || row.quantiles_bps || row.quantiles;
      const values = Object.entries(quantiles).sort(([a], [b]) => Number(a) - Number(b));
      const inBps = !!row.quantiles_bps;
      return { ...row, quantiles, prediction: row.display_prediction ?? row.prediction_bps ?? row.prediction,
        actual: row.display_actual !== undefined ? row.display_actual : inBps && row.actual != null ? Math.max(0, Math.expm1(row.actual)) : row.actual,
        baseline: row.display_baseline ?? (inBps ? Math.max(0, Math.expm1(row.baseline)) : row.baseline),
        band: values.length > 1 ? [values[0][1], values[values.length - 1][1]] : undefined };
    });
}

export const defaultForecastProgram = `def predict(context, parameters, state):
    # Arrays use target x history. Return target x horizon x quantile.
    return {"prediction": [[[values[-1] for q in context["quantiles"]]
                            for t in context["future_timestamps"]]
                           for values in context["target"]]}
`;

export interface ForecastObservationSession {
  id: number; model_version: number; name: string; config: Record<string, unknown>;
  is_active: boolean; pending_operation: string; last_error: string; last_origin: string | null;
}
export interface ForecastObservationStatus {
  session_id: number; active: boolean; pending_operation: string; last_error: string;
  warmup_paths: number; prospective_paths: number; complete_prospective_paths: number; scored_paths: number;
  config_sha256: string; artifact_sha256: string;
}
export interface ForecastObservationSnapshot extends ForecastObservationStatus {
  fresh: boolean;
  by_symbol: Record<string, { forecast_origin: string; unit: string; calibration_ready: boolean;
    horizons: { horizon: number; timestamp: string; quantiles: Record<string, number> }[] }>;
}
